import { releaseManifest, type ReleaseManifest } from '@/shared/release-manifest';
import { classifyReleaseCompatibility, type ReleaseCompatibilityStatus } from '@/shared/release-compatibility';
import { readReleaseManifestFromHeaderBag, releaseTransportBroadcastChannelName } from '@/shared/release-transport';

export type RuntimeReleaseObservationSource = 'api-header' | 'socket' | 'broadcast';

export type RuntimeReleaseState = Readonly<{
    bootManifest: ReleaseManifest;
    backendManifest: ReleaseManifest;
    status: ReleaseCompatibilityStatus;
    source: RuntimeReleaseObservationSource;
}>;

type RuntimeReleaseBroadcastPayload = Readonly<{
    type: 'runtime-release-observed';
    manifest: ReleaseManifest;
}>;

const createRuntimeReleaseState = ({
    backendManifest,
    source,
}: {
    backendManifest: ReleaseManifest;
    source: RuntimeReleaseObservationSource;
}): RuntimeReleaseState =>
    Object.freeze({
        bootManifest: releaseManifest,
        backendManifest,
        status: classifyReleaseCompatibility({
            frontend: releaseManifest,
            backend: backendManifest,
        }),
        source,
    });

let currentRuntimeReleaseState: RuntimeReleaseState = createRuntimeReleaseState({
    backendManifest: releaseManifest,
    source: 'api-header',
});

const listeners = new Set<() => void>();
let broadcastChannel: BroadcastChannel | null = null;

const isReleaseManifest = (value: unknown): value is ReleaseManifest => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const candidate = value as Partial<ReleaseManifest>;
    return (
        typeof candidate.revision === 'string' &&
        /^[0-9a-f]{40}$/i.test(candidate.revision) &&
        typeof candidate.protocolVersion === 'number' &&
        Number.isInteger(candidate.protocolVersion) &&
        candidate.protocolVersion > 0 &&
        typeof candidate.minSupportedFrontendProtocolVersion === 'number' &&
        Number.isInteger(candidate.minSupportedFrontendProtocolVersion) &&
        candidate.minSupportedFrontendProtocolVersion > 0
    );
};

const isBroadcastPayload = (value: unknown): value is RuntimeReleaseBroadcastPayload => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const candidate = value as Partial<RuntimeReleaseBroadcastPayload>;
    return candidate.type === 'runtime-release-observed' && isReleaseManifest(candidate.manifest);
};

const emitChange = () => {
    for (const listener of listeners) {
        listener();
    }
};

const cleanupBroadcastChannel = () => {
    if (!broadcastChannel) {
        return;
    }

    broadcastChannel.close();
    broadcastChannel = null;
};

const observeRuntimeReleaseManifestInternal = ({
    backendManifest,
    source,
}: {
    backendManifest: ReleaseManifest;
    source: RuntimeReleaseObservationSource;
}) => {
    const nextState = createRuntimeReleaseState({
        backendManifest,
        source,
    });

    if (
        nextState.status === currentRuntimeReleaseState.status &&
        nextState.source === currentRuntimeReleaseState.source &&
        nextState.backendManifest.revision === currentRuntimeReleaseState.backendManifest.revision &&
        nextState.backendManifest.protocolVersion === currentRuntimeReleaseState.backendManifest.protocolVersion &&
        nextState.backendManifest.minSupportedFrontendProtocolVersion ===
            currentRuntimeReleaseState.backendManifest.minSupportedFrontendProtocolVersion
    ) {
        return;
    }

    currentRuntimeReleaseState = nextState;
    emitChange();

    if (source === 'broadcast' || !broadcastChannel) {
        return;
    }

    broadcastChannel.postMessage({
        type: 'runtime-release-observed',
        manifest: backendManifest,
    } satisfies RuntimeReleaseBroadcastPayload);
};

const ensureBroadcastChannel = () => {
    if (broadcastChannel || typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
        return;
    }

    broadcastChannel = new BroadcastChannel(releaseTransportBroadcastChannelName);
    broadcastChannel.addEventListener('message', (event: MessageEvent<unknown>) => {
        if (!isBroadcastPayload(event.data)) {
            return;
        }

        observeRuntimeReleaseManifestInternal({
            backendManifest: event.data.manifest,
            source: 'broadcast',
        });
    });
};

export const getRuntimeReleaseStateSnapshot = (): RuntimeReleaseState => currentRuntimeReleaseState;

export const subscribeRuntimeReleaseState = (listener: () => void) => {
    listeners.add(listener);
    ensureBroadcastChannel();

    return () => {
        listeners.delete(listener);
    };
};

export const observeRuntimeReleaseManifest = ({
    backendManifest,
    source,
}: {
    backendManifest: ReleaseManifest;
    source: RuntimeReleaseObservationSource;
}) => {
    ensureBroadcastChannel();
    observeRuntimeReleaseManifestInternal({
        backendManifest,
        source,
    });
};

export const observeRuntimeReleaseHeaders = (headers: unknown) => {
    const parsedManifest = readReleaseManifestFromHeaderBag(headers);
    if (!parsedManifest) {
        return;
    }

    observeRuntimeReleaseManifest({
        backendManifest: parsedManifest,
        source: 'api-header',
    });
};

export const resetRuntimeReleaseStoreForTests = () => {
    listeners.clear();
    cleanupBroadcastChannel();
    currentRuntimeReleaseState = createRuntimeReleaseState({
        backendManifest: releaseManifest,
        source: 'api-header',
    });
};
